import { useEffect, useState, useCallback, useRef } from "react";
import { getActiveExecutionSubGroup } from "../services/executionSubGroup";

interface IuseFetchExecutionSubGroup {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
}

const useFetchExecutionSubGroup = ({
  setIsLoading,
  fetchOnMount = true, // Default to true for backward compatibility
}: IuseFetchExecutionSubGroup) => {
  const [executionSubGroupData, setExecutionSubGroupData] = useState<any>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const fetchExecutionSubGroup = useCallback(async () => {
    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      const result = await getActiveExecutionSubGroup();
      setExecutionSubGroupData(result);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching execution sub groups:", err);
      setExecutionSubGroupData([]);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  useEffect(() => {
    if (fetchOnMount) {
      fetchExecutionSubGroup();
    }
  }, [fetchOnMount, fetchExecutionSubGroup]);

  return [executionSubGroupData, fetchExecutionSubGroup, isFetching] as const;
};

export default useFetchExecutionSubGroup;
